import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useSEO } from "../lib/seo";
import { waLink } from "../lib/whatsapp";
import BlurHashImage from "../components/BlurHashImage";

const METHODS = [
  {
    title: "DTF Printing",
    short: "Colourful, detailed artwork",
    body: "Direct-to-Film printing is suitable for colourful and detailed custom designs, gradients, illustrations and photo-based artwork.",
    bestFor: ["Colourful artwork", "Detailed graphics", "Illustrations & photos", "Cotton and similar garments"],
    img: "/dtf-printing.png",
  },
  {
    title: "Vinyl Printing",
    short: "Names, numbers & lettering",
    body: "Vinyl is suitable for names, numbers, lettering and selected designs. It is a practical choice for personalised typography and jersey details.",
    bestFor: ["Names", "Numbers", "Text & lettering", "Selected jersey designs"],
    img: "/vinyl-printing.png",
  },
  {
    title: "Sublimation",
    short: "Polyester & sportswear",
    body: "Sublimation is suitable for polyester garments and sportswear, especially detailed or all-over designs.",
    bestFor: ["Polyester garments", "Sportswear", "Detailed designs", "All-over prints"],
    img: "/sublimation-printing.png",
  },
  {
    title: "Custom Design Support",
    short: "Help from artwork to print",
    body: "Have your own artwork? Send it to us and discuss your requirements through WhatsApp. We can help prepare the design for printing.",
    bestFor: ["Your own artwork", "Design preparation", "Print guidance", "WhatsApp support"],
    img: "/custom-design-support.png",
  },
];

export default function Printing() {
  useSEO({
    title: "Printing Methods — DTF, Vinyl & Sublimation | PrintMyTees",
    description: "Learn about DTF, Vinyl and Sublimation printing methods for custom apparel and find the method that suits your design.",
  });

  return (
    <div className="container-pmt py-10 sm:py-12" data-testid="printing-page">
      <div className="max-w-3xl">
        <div className="overline">Printing Methods</div>
        <h1 className="display-h2 mt-2">How we print your idea.</h1>
        <p className="mt-4 text-stone-600">
          Different garments and artwork need different printing methods. Explore the options below and choose the one that fits your product and design.
        </p>
      </div>

      <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {METHODS.map((m, index) => (
          <div
            key={m.title}
            className="card-pmt overflow-hidden group"
            data-testid={`printing-${m.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <div className="aspect-[16/10] bg-[#F3EDE2] overflow-hidden">
              <BlurHashImage
                src={m.img}
                alt={`${m.title} printing method`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                priority={index < 2}
                width={800}
                height={500}
              />
            </div>
            <div className="p-5 sm:p-6">
              <div className="text-xs font-mono text-[#D95328]">0{index + 1}</div>
              <div className="font-bold uppercase text-xl mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                {m.title}
              </div>
              <div className="mt-1 text-sm font-semibold text-stone-800">{m.short}</div>
              <p className="mt-2 text-sm text-stone-600">{m.body}</p>

              <div className="mt-3 p-3 bg-[#FAF7F2] border border-[#E6DFD5] text-sm text-stone-700">
                <span className="font-semibold text-stone-900">Choose this when:</span>{" "}
                {m.title === "DTF Printing" ? "you need detailed, colourful artwork on a garment." : m.title === "Vinyl Printing" ? "you need names, numbers or clean lettering." : m.title === "Sublimation" ? "you are printing detailed artwork on polyester or sportswear." : "you have artwork and want help preparing it for printing."}
              </div>

              <div className="mt-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-500">Best for</div>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {m.bestFor.map((item) => (
                    <div key={item} className="text-sm text-stone-700 flex items-start gap-2">
                      <span className="text-[#D95328]">✓</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <section className="mt-12 sm:mt-16 border-t border-[#E6DFD5] pt-10">
        <div className="overline">Quick guide</div>
        <h2 className="display-h3 mt-2">Which method fits your design?</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {[
            ["DTF", "Detailed & colourful artwork", "Great for illustrations, graphics and photo-style prints."],
            ["Vinyl", "Names, numbers & text", "Useful for personalised lettering and selected jersey designs."],
            ["Sublimation", "Polyester & sportswear", "Designed for polyester garments and detailed sportswear prints."],
          ].map(([name, title, text]) => (
            <div key={name} className="card-pmt p-4 sm:p-5">
              <div className="font-mono text-xs text-[#D95328]">{name}</div>
              <div className="font-semibold mt-2">{title}</div>
              <p className="text-sm text-stone-600 mt-1">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 sm:mt-12 card-pmt p-5 sm:p-7 bg-[#F3EDE2]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
          <div>
            <div className="overline">Not sure which one?</div>
            <h2 className="display-h3 mt-2">Send us your design.</h2>
            <p className="mt-2 text-sm text-stone-600">
              Tell us what you want to print and we'll help you choose a suitable method. You can send your artwork on WhatsApp if you need help deciding.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row md:justify-end gap-3">
            <Link to="/custom-orders" className="btn-primary justify-center focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2">Get a Custom Quote</Link>
            <a href={waLink("Hi PrintMyTees, I need help choosing a printing method.")} target="_blank" rel="noreferrer" className="btn-whatsapp justify-center focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2">
              <MessageCircle size={14} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
